import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, fromEvent } from 'rxjs';
import { map, share, startWith } from 'rxjs/operators';
import { DashboardMenuService } from '../menu/dashboard-menu.service';
import {
	BREADCRUMB_HEIGHT,
	BREAKPOINT_LG,
	BREAKPOINT_MD,
	BREAKPOINT_SM,
	MENU_WIDTH_AT_BREAKPOINT_SM,
	NAV_TABS_HEIGHT,
	NAVBAR_HEIGHT,
	NAVBAR_SPACING,
	NAVBAR_SPACING_XS,
	SPACING,
	SPACING_XS
} from './dashboard-layout-constants';
import { DashboardLayoutDimensions } from './dashboard-layout-dimensions';


export interface AutoNavTabs {
	hasMinWidth?: boolean,
	hasMinHeight?: boolean,
	hasMultipleTabs?: boolean,
}

// TODO: Should this be named DashboardLayoutService and moved to Common?

@Injectable({ providedIn: 'root' })
export class DashboardLayoutService {

	public menuService = inject(DashboardMenuService);

	private readonly _dimensions = signal<DashboardLayoutDimensions>(
		this.calcDimensions(window, this.menuService.calcMenuWidth(window), true)
	);
	public readonly dimensions = this._dimensions.asReadonly();

	/**
	 * @deprecated
	 * This is still used with bootstrap dialogs but not mat dialogs
	 */
	public dialogHeight = signal(0);


	/**
	 * By default, the navigation tabs will automatically hide on small
	 * devices (not wide enough) or if there is only a single tab. However,
	 * the folder icon under th e user menu can be used to force the tabs
	 * to be shown or hidden. Resizing the screen forces the visibility
	 * back to 'Auto'. Set to 'Hide' to make more vertical room above the
	 * fold.
	 */
	public showNavTabsVisibility = signal<'Show' | 'Hide' | 'Auto'>('Auto');


	/**
	 * If showNavTabsVisibility$ is 'Auto' then showNavTabsAuto$ will
	 * determine whether the navigation tabs are shown or hidden.
	 */
	private showNavTabsAuto = signal<AutoNavTabs>({ hasMinHeight: true, hasMinWidth: true, hasMultipleTabs: true });


	/**
	 * Determines if the nav tabs should be visible if
	 * showNavTabsVisibility$ == 'Auto 
	 */
	public setShowNavTabsAuto(value: AutoNavTabs) {
		const current = this.showNavTabsAuto();

		this.showNavTabsAuto.set({
			hasMinHeight: value.hasMinHeight ?? current.hasMinHeight,
			hasMinWidth: value.hasMinWidth ?? current.hasMinWidth,
			hasMultipleTabs: value.hasMultipleTabs ?? current.hasMultipleTabs,
		});
	}


	/**
	 * The showNavTabs$ observable defines whether the last breadcrumbs menu
	 * is displayed as navigation tabs directly under the breadcrumbs. 
	 */
	public showNavTabs = computed(() => {
		const visibility = this.showNavTabsVisibility();
		const auto = this.showNavTabsAuto();
		if (visibility == 'Show') return true;
		else if (visibility == 'Hide') return false;
		else return auto.hasMinHeight && auto.hasMinWidth && auto.hasMultipleTabs;
	});



	constructor() {

		//
		// Listen for the window resizing
		//
		const resize$ = fromEvent(window, 'resize')
			.pipe(
				startWith({ target: window }),
			);

		resize$.subscribe((e) => {
			this.showNavTabsVisibility.set('Auto');
			this.setShowNavTabsAuto({
				hasMinHeight: (e.target as Window).innerHeight > 500,
				hasMinWidth: (e.target as Window).innerWidth > BREAKPOINT_MD,
			})
			this.setDialogHeight((e.target as Window).innerWidth, (e.target as Window).innerHeight);
		});


		//
		// Every time the window resizes or the menu width is shown,
		// calculate and emit the new dimensions.
		//
		combineLatest([resize$, toObservable(this.menuService.menuWidth), toObservable(this.showNavTabs)])
			.pipe(
				map(([e, menuWidth, showNavTabs]) => this.calcDimensions(e.target as Window, menuWidth, showNavTabs ?? true)),
				share()
			)
			.subscribe(d => this._dimensions.set(d));
	}

	// to calculate the height of the dialog
	setDialogHeight(width: number, height: number) {
		// height of a modal is calc(100% - var(--bs-modal-margin) * 2);
		// --bs-modal-margin is 1.75rem for width>= 576px and 0.5rem for width<576px
		let dialogHeight = 0;
		if (width < 576) {
			dialogHeight = height - 2 * 0.5 * 16;
		} else {
			dialogHeight = height - 2 * 1.75 * 16;
		}
		this.dialogHeight.set(dialogHeight);
	}


	/**
	 * Calculate all of the layout sizes. This is called each time the window
	 * is resized of the menu is opened/closed.
	 * @param window The DOM Window object
	 * @param menuWidth The width of the left menu which may be full, collapsed, or hidden.
	 */
	private calcDimensions(window: Window, menuWidth: number, showNavTabs: boolean): DashboardLayoutDimensions {

		//
		// The inner values are the browser window size 
		//
		const w = window.innerWidth;
		const h = window.innerHeight;

		//
		// Determine what breakpoint range the window width equates to
		//
		let breakpoint: 'xs' | 'sm' | 'md' | 'lg' = 'xs';

		if (w < BREAKPOINT_SM) breakpoint = 'xs';
		else if (w < BREAKPOINT_MD) breakpoint = 'sm';
		else if (w < BREAKPOINT_LG) breakpoint = 'md';
		else breakpoint = 'lg';

		//
		// These calculations should align with what is in ACS.page.scss
		//
		let d: DashboardLayoutDimensions = {
			menuWidth,
			breakpoint,
			screenWidth: window.outerWidth,
			screenHeight: window.outerHeight,
			windowWidth: w,
			windowHeight: h,
			contentWidth: w - 2 * SPACING_XS - 1,	// xs (0 - 543)
			contentHeight: h - NAVBAR_HEIGHT - 3 - NAVBAR_SPACING_XS - BREADCRUMB_HEIGHT, //xs (0 - 543)
			contentWidthSnugXS: 0,
			contentHeightSnugXS: 0,
		};


		if (breakpoint == 'sm') { //544 - 767
			d = Object.assign(d, {
				contentWidth: w - MENU_WIDTH_AT_BREAKPOINT_SM - 2 * SPACING - 1,
				contentHeight: h - NAVBAR_HEIGHT - 3 - NAVBAR_SPACING - BREADCRUMB_HEIGHT,
			});
		}
		else if (breakpoint == 'md') { //768 - 1279
			d = Object.assign(d, {
				contentWidth: w - menuWidth - 2 * SPACING - 1,
				contentHeight: h - NAVBAR_HEIGHT - 3 - NAVBAR_SPACING - BREADCRUMB_HEIGHT,
			});
		}

		else if (breakpoint == 'lg') { //1280+
			d = Object.assign(d, {
				contentWidth: w - menuWidth - 2 * SPACING - 1,
				contentHeight: h - NAVBAR_HEIGHT - NAVBAR_SPACING - BREADCRUMB_HEIGHT - SPACING,
			});
		}

		if (showNavTabs) d.contentHeight -= NAV_TABS_HEIGHT;


		const spacing = breakpoint == 'xs' ? SPACING_XS : SPACING;

		if (breakpoint == 'xs') {
			d.contentWidthSnugXS = d.contentWidth + 2 * spacing;
			d.contentHeightSnugXS = d.contentHeight + spacing - 7;
		}
		else {
			d.contentWidthSnugXS = d.contentWidth;
			d.contentHeightSnugXS = d.contentHeight;
		}

		return d;
	}


	/**
	 * Determines if a dom element is scrollable. Use @ViewChild(...) to get an ElementRef
	 * and then pass in the ElementRef.nativeElement property to this function. 
	 */
	isScrollable(el: HTMLInputElement) {

		// The scrollTop() method sets or returns the vertical
		// scrollbar position for the selected elements
		const y1 = el.scrollTop;
		el.scrollTop += 1;
		const y2 = el.scrollTop;
		el.scrollTop -= 1;
		const y3 = el.scrollTop;
		el.scrollTop = y1;

		// The scrollLeft() method sets or returns the horizontal  
		// scrollbar position for the selected elements
		const x1 = el.scrollLeft;
		el.scrollLeft += 1;
		const x2 = el.scrollLeft;
		el.scrollLeft -= 1;
		const x3 = el.scrollLeft;
		el.scrollLeft = x1;

		return {
			horizontallyScrollable: x1 !== x2 || x2 !== x3,
			verticallyScrollable: y1 !== y2 || y2 !== y3
		}
	}

}